import React, { MouseEventHandler, useEffect, useMemo, useState } from 'react'

import { ThemeProvider } from '@mui/material'
import { getTheme } from 'themes'

import { DashboardVariantType, ILayoutItem } from 'types/GlobalDashboard'

import Box from 'components_new/atoms/Box'
import Icon from 'components_new/atoms/Icon'
import IconButton from 'components_new/atoms/IconButton'

import OptionsMenu from 'components_new/molecules/OptionsMenu'

import CopyToDashboardDialog from '../dialogs/CopyToDashboardDialog'
import DeleteDialog from '../dialogs/DeleteDialog'
import EmbeddedDialog from '../dialogs/EmbeddedDialog'

import * as UserEvents from 'redux/api/internal/UserEvents'
import { useDashboardWidgetActions } from 'redux/actions/Widgets/DashboardWidgets'

import { Action } from 'types/GlobalUserEvents'
import {
  FormattedWidgetData,
  isDashboardWidgetFullyLoaded,
  isDashboardWidgetSettingsLoaded,
  ParsedSegmentPath,
  TDashboardWidget,
  WidgetType
} from 'types/GlobalWidget'
import { ParsedDashboardFilterOptions } from 'types/GlobalDashboardFilter'
import { DETAILS_DISABLE_TEXT } from 'utils/constants'
import { useTranslation } from 'language/translator'
import { isFormattedData } from 'utils/widgetData'
import { useWidgetActions } from 'redux/actions/Widgets'

const NON_DATA_WIDGET_TYPES = [
  WidgetType.TEXT,
  WidgetType.COMMENT,
  WidgetType.IMAGE
]

interface WidgetMenuProps {
  availableSpace: ILayoutItem | null
  customSegmentBy: string | null
  dashboardFilterOptions: Map<string, ParsedDashboardFilterOptions>
  editMode: boolean
  formattedData: FormattedWidgetData | null
  isHomepalOrAdmin: boolean
  loading: boolean
  mobile: boolean
  embedded: boolean
  fullSize: boolean
  scaleFactor: number
  setEdit: () => void
  setFullSizeDialogOpen: (arg0: boolean) => void
  showMenu?: boolean
  showUnderlyingContent: (
    segmentPaths: ParsedSegmentPath[],
    initialKpiOptionId: string | null
  ) => void
  variantType: DashboardVariantType
  widget: TDashboardWidget
}

const WidgetMenu = (props: WidgetMenuProps) => {
  const {
    availableSpace,
    customSegmentBy,
    dashboardFilterOptions,
    editMode,
    embedded,
    formattedData,
    fullSize,
    isHomepalOrAdmin,
    loading,
    mobile,
    scaleFactor,
    setEdit,
    setFullSizeDialogOpen,
    showMenu = true,
    showUnderlyingContent,
    variantType,
    widget
  } = props

  const { t } = useTranslation()
  const { tryExportWidgetDataXlsx } = useWidgetActions()
  const {
    tryCreateDashboardWidget,
    tryUpdateDashboardWidget,
    tryDeleteDashboardWidget
  } = useDashboardWidgetActions()

  const [copyToDashboardDialogOpen, setCopyToDashboardDialogOpen] =
    useState<boolean>(false)

  const [anchorPosition, setAnchorPosition] = useState<{
    x: number
    y: number
  } | null>(null)
  const open = Boolean(anchorPosition)

  const handleClick: MouseEventHandler<HTMLElement> = (event) => {
    event.stopPropagation()
    event.preventDefault()

    setAnchorPosition({ x: event.clientX, y: event.clientY })
  }

  const handleClose = () => {
    setAnchorPosition(null)
  }

  const handleExport = () => {
    if (
      isFormattedData(formattedData) &&
      isDashboardWidgetSettingsLoaded(widget)
    ) {
      tryExportWidgetDataXlsx(
        widget.settings,
        formattedData,
        widget.title,
        t('widgets', 'total_column'),
        t('widgets', 'variant_options_row_header')
      )

      UserEvents.log({
        action: Action.WIDGET_DATA_EXPORTED,
        context: widget.type,
        dashboard_id: widget.dashboard_id
      })
    }
  }

  useEffect(() => {
    if (!showMenu) {
      handleClose()
    }
  }, [showMenu])

  const hideDetails = useMemo(() => {
    if (isDashboardWidgetSettingsLoaded(widget)) {
      return !!widget.settings.kpi_options.every(
        (option) => !option.can_show_details
      )
    }

    return true
  }, [widget.settings?.kpi_options])

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [embeddedDialogOpen, setEmbeddedDialogOpen] = useState(false)

  // Mobile and COMMENT in not edit mode does not support menu.
  if (mobile || (widget.type === WidgetType.COMMENT && !editMode)) return null

  const widgetWithData = !NON_DATA_WIDGET_TYPES.includes(widget.type)

  const ICON_BUTTON_SIZE = fullSize ? 32 : 26
  const ICON_SIZE = fullSize ? 24 : 20

  const isPublic = location.pathname.includes('public')

  const getDisabledTitle = () => {
    if (isPublic) {
      return t('global', 'login_required')
    } else {
      return undefined
    }
  }

  return (
    <>
      {!embedded || variantType === DashboardVariantType.API ? (
        <Box
          className="widget-menu"
          sx={{
            position: 'absolute',
            height: 'auto',
            width: 'auto',
            top: 0,
            right: 0,
            transition: 'all 0.5s ease',
            pt: 1.5 * scaleFactor,
            px: 1.5 * scaleFactor,
            pb: 0.75 * scaleFactor,
            zIndex: 3
          }}
        >
          {/*-- buttons --*/}
          <Box sx={{ display: 'flex', flexWrap: 'nowrap' }}>
            {widget.type !== WidgetType.TEXT &&
            widget.type !== WidgetType.COMMENT ? (
                  <IconButton
                    color="disabled"
                    onClick={() => {
                      if (!fullSize) {
                        UserEvents.log({
                          action: Action.WIDGET_ENLARGED,
                          context: widget.type,
                          dashboard_id: widget.dashboard_id,
                          dashboard_group_id: null
                        })
                      }

                      setFullSizeDialogOpen(!fullSize)
                    }}
                    onMouseDown={(event) => event.stopPropagation()}
                    size="small"
                    sx={{
                      width: ICON_BUTTON_SIZE,
                      height: ICON_BUTTON_SIZE
                    }}
                  >
                    <Icon
                      name={!fullSize ? 'ZoomInOutlined' : 'ZoomOutOutlined'}
                      sx={{
                        fontSize: ICON_SIZE
                      }}
                    />
                  </IconButton>
                ) : null}

            {!embedded &&
            !customSegmentBy &&
            editMode &&
            !loading &&
            widget.type !== WidgetType.COMMENT ? (
                  <IconButton
                    color="disabled"
                    onClick={() => setEdit()}
                    onMouseDown={(event) => event.stopPropagation()}
                    size="small"
                    sx={{
                      width: ICON_BUTTON_SIZE,
                      height: ICON_BUTTON_SIZE
                    }}
                  >
                    <Icon
                      fontSize="small"
                      name="Edit"
                      sx={{
                        fontSize: ICON_SIZE
                      }}
                    />
                  </IconButton>
                ) : null}
            {variantType === DashboardVariantType.PUBLIC ||
            (!widgetWithData && !editMode) ? null : (
                <IconButton
                  color="disabled"
                  onClick={handleClick}
                  onMouseDown={(event) => event.stopPropagation()}
                  size="small"
                  sx={{
                    width: ICON_BUTTON_SIZE,
                    height: ICON_BUTTON_SIZE
                  }}
                >
                  <Icon
                    fontSize="small"
                    name="MoreVertOutlined"
                    sx={{
                      fontSize: ICON_SIZE
                    }}
                  />
                </IconButton>
              )}
          </Box>

          {/*-- background --*/}
          <Box
            sx={{
              bgcolor: 'background.widget',
              position: 'absolute',
              height: '100%',
              width: '100%',
              top: 0,
              right: 0,
              opacity: 0.9,
              borderBottomLeftRadius: 8 * scaleFactor,
              borderBottomRightRadius: customSegmentBy
                ? 8 * scaleFactor
                : undefined,
              zIndex: -1
            }}
          />
        </Box>
      ) : null}

      {/*-- menu --*/}
      <ThemeProvider theme={getTheme('light')}>
        <OptionsMenu
          anchorReference="anchorPosition"
          anchorPosition={
            anchorPosition
              ? { top: anchorPosition?.y, left: anchorPosition?.x }
              : undefined
          }
          disableScrollLock={true}
          onClose={handleClose}
          open={open && showMenu}
          options={[
            // show details
            ...(widgetWithData
              ? [
                  {
                    disabled: isPublic || hideDetails,
                    divider:
                      editMode ||
                      (widget.type !== WidgetType.KEY_FIGURE &&
                        widget.type !== WidgetType.FUNNEL),
                    iconName: 'Explore' as const,
                    infoTip: hideDetails
                      ? DETAILS_DISABLE_TEXT
                      : getDisabledTitle(),
                    onClick: () => {
                      showUnderlyingContent([], null)
                    },
                    title: t('global', 'explore')
                  }
                ]
              : []),
            // export to excel
            ...(widgetWithData &&
            widget.type !== WidgetType.KEY_FIGURE &&
            widget.type !== WidgetType.FUNNEL &&
            !loading
              ? [
                  {
                    disabled: isPublic,
                    iconName: 'Download' as const,
                    infoTip: getDisabledTitle(),
                    onClick: () => handleExport(),
                    title: t('global', 'export_to_excel')
                  }
                ]
              : []),
            // embed
            ...(editMode &&
            widgetWithData &&
            isHomepalOrAdmin &&
            isDashboardWidgetSettingsLoaded(widget)
              ? [
                  {
                    divider: true,
                    iconBadge: widget.settings.embeddable,
                    iconName: 'CodeOutlined' as const,
                    onClick: () => {
                      setEmbeddedDialogOpen(true)
                    },
                    title: t('global', 'embed')
                  }
                ]
              : []),
            // duplicate
            ...(editMode
              ? [
                  {
                    disabled: !availableSpace,
                    iconName: 'Duplicate' as const,
                    onClick: () => {
                      if (availableSpace) {
                        tryCreateDashboardWidget({
                          dashboard_id: widget.dashboard_id,
                          kpi_id: null,
                          widget_id: widget.id,
                          width: availableSpace.w,
                          height:
                            widget.type === WidgetType.TEXT
                              ? 1
                              : availableSpace.h,
                          x: availableSpace.x,
                          y: availableSpace.y
                        })
                      }
                    },
                    title: t('global', 'duplicate'),
                    tooltip: availableSpace
                      ? undefined
                      : t('dashboards', 'dashboard_full')
                  }
                ]
              : []),
            // copy
            ...(editMode
              ? [
                  {
                    divider: true,
                    iconName: 'DriveFileMoveOutlined' as const,
                    onClick: () => {
                      setCopyToDashboardDialogOpen(true)
                    },
                    title: t('global', 'copy_to')
                  }
                ]
              : []),
            // delete
            ...(editMode
              ? [
                  {
                    iconName: 'Delete' as const,
                    onClick: () => {
                      setDeleteDialogOpen(true)
                    },
                    title: t('global', 'delete')
                  }
                ]
              : [])
          ]}
        />
      </ThemeProvider>

      {/*-- dialogs --*/}
      <ThemeProvider theme={getTheme('light')}>
        <DeleteDialog
          open={deleteDialogOpen}
          onClose={() => setDeleteDialogOpen(false)}
          title={widget.title}
          handleDelete={() => {
            tryDeleteDashboardWidget(widget.id, widget.dashboard_id)

            setDeleteDialogOpen(false)
          }}
        />

        {isDashboardWidgetFullyLoaded(widget) ? (
          <EmbeddedDialog
            dashboardFilterOptions={dashboardFilterOptions}
            open={embeddedDialogOpen}
            onClose={() => setEmbeddedDialogOpen(false)}
            onChange={(embeddable) =>
              tryUpdateDashboardWidget(widget.id, { data: { embeddable } })
            }
            loading={loading}
            widget={widget}
          />
        ) : null}

        {isFormattedData(formattedData) &&
        isDashboardWidgetFullyLoaded(widget) ? (
              <CopyToDashboardDialog
                formattedData={formattedData}
                onClose={() => setCopyToDashboardDialogOpen(false)}
                open={copyToDashboardDialogOpen}
                widget={widget}
              />
            ) : null}
      </ThemeProvider>
    </>
  )
}

export default WidgetMenu
